import { AxiosError } from "axios";
import useSWR from "swr";
import {
  GetStatistics200Response,
  InstrumentTypeResponse,
} from "../../../codegen-api";
import { APIEndpointEnum } from "../../../enums/endpoint";
import { publicApi } from "../../../services/api/apiFetcher";
import { pollingInterval } from "../../../services/api/pollingInterval";
import { useLoggerMiddleware } from "../middleware/useLoggerMiddleware";

export type SpotStatisticsByAsset = Record<
  string,
  GetStatistics200Response | undefined
>;

/**
 * `/statistics` for a list of spot markets, keyed by asset.
 *
 * The trading header reads a spot market's 24h change and volume from this
 * endpoint; lists that show the same markets (palette, watchlist, prices
 * widget) read it here so they quote the same numbers instead of "-". There is
 * no batch endpoint, so it is one request per market, made only while
 * `enabled`. A market whose request fails is left out (unknown), never zeroed.
 */
export const useGetSpotStatistics = (assets: string[], enabled = true) => {
  const fetcher = publicApi();
  const key = assets.length && enabled ? [...assets].sort().join(",") : "";

  return useSWR<SpotStatisticsByAsset, AxiosError>(
    key ? [APIEndpointEnum.STATISTICS, "spot", key] : undefined,
    async () => {
      const entries = await Promise.all(
        key.split(",").map(async (asset) => {
          try {
            const { data } = await (
              await fetcher.getStatistics(
                asset as any,
                InstrumentTypeResponse.Spot
              )
            )();
            return [asset, data] as const;
          } catch {
            return [asset, undefined] as const;
          }
        })
      );
      return Object.fromEntries(entries);
    },
    {
      use: [useLoggerMiddleware],
      refreshInterval: pollingInterval[APIEndpointEnum.STATISTICS],
      dedupingInterval: 10000,
      revalidateOnFocus: false,
    }
  );
};
